/**
 * Полоска у исхода → список живых контор → третья плитка той же карточки.
 *
 * Полоска обещает выбор, строка списка — собрать плитку, кнопка плитки —
 * открыть контору. Смешать эти жесты — снова Grab как ссылка.
 */

import { useEffect, useState } from 'react';

import {
  type CoverFail,
  type CoverQuote,
  type PricedCover,
  coverLeg,
  coverPctText,
  coverPrice,
  fetchCovers,
  pricedCovers,
} from '../lib/cover';
import type { RowLeg } from '../lib/row';
import { useSettings } from '../store/settings';
import * as act from './act';
import { Brand } from './Brand';
import { copy, Grab } from './Grab';
import { rub } from './format';
import { venueName } from './meta';
import type { Meta } from './meta';
import { useFilters } from './filters';
import './cover.css';

const STALE_S = 8;

export function CoverStrip({
  open,
  label,
  onToggle,
}: {
  open: boolean;
  label: string;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={`nx-cover__strip${open ? ' is-open' : ''}`}
      aria-expanded={open}
      aria-label={`другие конторы на ${label}`}
      title={`другие конторы на ${label}`}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <i className="nx-cover__rail" aria-hidden="true" />
    </button>
  );
}

const FAIL_SAY = {
  denied: 'перекрытие открывается с оплаченным доступом',
  gone: 'вилки больше нет в ленте',
  fail: 'не удалось спросить',
} as const;

export function CoverList({
  quotes,
  oppositeOdds,
  bank,
  meta,
  fail,
  onPick,
}: {
  quotes: readonly CoverQuote[] | null;
  oppositeOdds: string;
  bank: number;
  meta: Meta;
  fail?: CoverFail;
  onPick: (quote: CoverQuote) => void;
}): React.ReactElement {
  const venues = useFilters((s) => s.venues);
  const venueMode = useFilters((s) => s.venueMode);
  if (fail !== undefined) {
    return (
      <div className="nx-cover" onPointerDown={(event) => event.stopPropagation()}>
        <div className="nx-cover__empty">{FAIL_SAY[fail]}</div>
      </div>
    );
  }
  if (quotes === null) {
    return (
      <div className="nx-cover" onPointerDown={(event) => event.stopPropagation()}>
        <div className="nx-cover__wait">ищем</div>
      </div>
    );
  }
  const rows = pricedCovers(quotes, oppositeOdds, String(bank), venues, venueMode);
  if (rows.length === 0) {
    return (
      <div className="nx-cover" onPointerDown={(event) => event.stopPropagation()}>
        <div className="nx-cover__empty">сейчас никого нет</div>
      </div>
    );
  }
  return (
    <div className="nx-cover" role="listbox" onPointerDown={(event) => event.stopPropagation()}>
      {rows.map((row, i) => (
        <CoverRow
          key={row.quote.s}
          row={row}
          best={i === 0}
          meta={meta}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

function CoverRow({
  row,
  best,
  meta,
  onPick,
}: {
  row: PricedCover;
  best: boolean;
  meta: Meta;
  onPick: (quote: CoverQuote) => void;
}): React.ReactElement {
  const down = row.price.profit.lt(0);
  const age = row.quote.age ?? 0;
  const href = row.quote.betUrl ?? row.quote.url ?? null;
  return (
    <button
      type="button"
      role="option"
      className={`nx-cover__row${best ? ' nx-cover__row--best' : ''}${down ? ' is-down' : ''}`}
      onClick={(event) => {
        event.stopPropagation();
        onPick(row.quote);
      }}
    >
      <span className="nx-cover__who">
        <Brand code={row.quote.s} name={venueName(meta, row.quote.s)} size="s" />
        <b>{venueName(meta, row.quote.s)}</b>
      </span>
      <span className="nx-cover__odds">{row.quote.odds}</span>
      <span className="nx-cover__pct">{coverPctText(row.price.roi)}</span>
      <span className="nx-cover__rub">{moneySay(row.price.profit.toNumber())}</span>
      {age >= STALE_S && <i className="nx-cover__age">{age} с</i>}
      {href === null && <i className="nx-cover__noin">входа нет</i>}
    </button>
  );
}

export function CoverDock({
  forkId,
  side,
  open,
  oppositeOdds,
  meta,
  onPick,
}: {
  forkId: string;
  side: string;
  open: boolean;
  oppositeOdds: string;
  meta: Meta;
  onPick: (quote: CoverQuote) => void;
}): React.ReactElement | null {
  const bank = useSettings((s) => s.bank);
  const [quotes, setQuotes] = useState<CoverQuote[] | null>(null);
  const [fail, setFail] = useState<CoverFail | undefined>(undefined);
  useEffect(() => {
    if (!open) return;
    let gone = false;
    setQuotes(null);
    setFail(undefined);
    void fetchCovers(forkId, side).then((row) => {
      if (gone) return;
      if (row.ok) {
        setQuotes(row.quotes);
        setFail(undefined);
        return;
      }
      setQuotes([]);
      setFail(row.reason);
    });
    return () => {
      gone = true;
    };
  }, [open, forkId, side]);
  if (!open) return null;
  return (
    <CoverList
      quotes={quotes}
      oppositeOdds={oppositeOdds}
      bank={bank}
      meta={meta}
      fail={fail}
      onPick={onPick}
    />
  );
}

export function CoverTile({
  forkId,
  quote,
  oppositeOdds,
  pickLabel,
  meta,
}: {
  forkId: string;
  quote: CoverQuote;
  oppositeOdds: string;
  pickLabel: string;
  meta: Meta;
}): React.ReactElement {
  const bank = useSettings((s) => s.bank);
  const price = coverPrice(oppositeOdds, quote.odds, String(bank));
  const stake = price.stakes[1].toNumber();
  const leg = coverLeg(quote, stake);
  const amount = `${rub(leg.rub)} ₽`;
  const href = leg.url;
  return (
    <li className="nx-det__step">
      <span className="nx-det__top">
        <span className="nx-det__no" aria-hidden="true">
          3
        </span>
        <Brand code={leg.source} name={venueName(meta, leg.source)} />
        <span className="nx-det__who">
          <em>перекрытие</em>
        </span>
      </span>
      <div className="nx-det__take">
        <span className="nx-det__pick">
          <Grab text={pickLabel} what="исход">
            {pickLabel}
          </Grab>
        </span>
        <span className="nx-det__odds">кэф {leg.odds}</span>
        <span className="nx-det__amount">
          <b>{amount}</b>
          <button
            type="button"
            className="nx-det__copy"
            title={`скопировать ${amount}`}
            aria-label={`скопировать ${amount}`}
            onClick={() => void copy(String(Math.round(leg.rub)))}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
              <path d="M10.5 3.5v-1h-8v8h1" />
            </svg>
          </button>
        </span>
      </div>
      <button
        type="button"
        className="nx-det__go"
        onClick={() => {
          void copy(String(Math.round(leg.rub)));
          if (href !== null) act.openBook(forkId, leg);
        }}
      >
        {href === null ? `скопировать ${amount}` : `открыть и скопировать ${amount}`}
      </button>
      <span className={`nx-det__entry ${href === null ? 'none' : ''}`}>
        {href === null
          ? 'входа нет — контору и матч искать руками'
          : leg.precision === 'outcome'
            ? 'откроется форма ставки'
            : leg.precision === 'event'
              ? 'откроется событие — исход выбрать самому'
              : 'откроется раздел — матч и исход искать самому'}
      </span>
    </li>
  );
}

export function CoverSum({
  oppositeOdds,
  quote,
  onClear,
}: {
  oppositeOdds: string;
  quote: CoverQuote;
  onClear: () => void;
}): React.ReactElement {
  const bank = useSettings((s) => s.bank);
  const price = coverPrice(oppositeOdds, quote.odds, String(bank));
  const down = price.profit.lt(0);
  return (
    <div className={`nx-cover__own${down ? ' is-down' : ''}`}>
      перекрытие · {coverPctText(price.roi)} · {moneySay(price.profit.toNumber())}
      <button type="button" className="nx-cover__drop" onClick={onClear}>
        убрать третью
      </button>
    </div>
  );
}

function moneySay(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return '0 ₽';
  return `${rounded > 0 ? '+' : ''}${rub(value)} ₽`;
}

export function oppositeLeg(legs: readonly RowLeg[], side: string): RowLeg | undefined {
  return legs.find((leg) => leg.side !== side);
}
